/**
 * mapParcoursPro — fonctions pures de conversion du modèle API (Professional)
 * vers les props du composant <ParcoursPro/>.
 *
 * Modèle source (DRF, DATE_FORMAT "%d-%m-%Y") :
 *  - Expérience/Formation : ParcoursPro { id, Name, entreprise:{Name,city,Logo…},
 *      BeginDate "DD-MM-YYYY", EndDate "DD-MM-YYYY"|null, CurrentPoste (bool),
 *      City, ContractType, KeyCompetencies_list:[{id,CompetencesType,CompetencesName…}] }
 *  - Compétence : Competence { id, CompetencesType (Métier|Interperso|Linguistique),
 *      CompetencesName }
 *
 * Cible :
 *  - Experience { id, title, company, location?, start ISO, end ISO|null, contract?, tags? }
 *  - Formation  { id, title, school, start, end, type: Diplôme|Formation|Titre|Certification }
 *  - Skill      { id, name, type, level?, suggested }
 */

/** "DD-MM-YYYY" → "YYYY-MM-DD". Tolère l'ISO déjà en place. Sinon null. */
export function ddmmyyyyToIso(value) {
  if (!value) return null
  const str = String(value).trim()
  const fr = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str)
  if (fr) return `${fr[3]}-${fr[2]}-${fr[1]}`
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10)
  return null
}

/**
 * Déduit le type d'une formation depuis son intitulé (pas de champ dédié
 * dans le modèle ParcoursPro). Par défaut : "Formation".
 */
export function inferFormationType(name) {
  const n = String(name ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // sans accents
  if (/certifi|certified|certificat/.test(n)) return 'Certification'
  if (/\btitre\b/.test(n)) return 'Titre'
  if (/diplome|\bbts\b|\bdut\b|\bbut\b|\bcap\b|\bbep\b|baccalaureat|\bbac\b|licence|master\b|\bmba\b|doctorat|ingenieur/.test(n)) return 'Diplôme'
  return 'Formation'
}

/**
 * Mappe une ligne ParcoursPro (ParcoursType=Expérience) vers Experience.
 * - CurrentPoste → end: null (poste en cours)
 * - sans EndDate ni CurrentPoste → end = start (terminé, durée nulle),
 *   pour ne pas afficher un faux "en cours"
 * - start manquant → repli sur EndDate, sinon todayIso
 */
export function mapExperience(row, todayIso) {
  const start = ddmmyyyyToIso(row.BeginDate) ?? ddmmyyyyToIso(row.EndDate) ?? todayIso
  const end = row.CurrentPoste ? null : ddmmyyyyToIso(row.EndDate) ?? start
  return {
    id: row.id,
    title: row.Name ?? '',
    company: row.entreprise?.Name ?? '',
    location: row.City ?? undefined,
    start,
    end,
    contract: row.ContractType ?? undefined,
    tags: (row.KeyCompetencies_list ?? []).map((c) => c.CompetencesName).filter(Boolean),
  }
}

/** Mappe une ligne ParcoursPro (ParcoursType=Formation) vers Formation. */
export function mapFormation(row, todayIso) {
  const start = ddmmyyyyToIso(row.BeginDate) ?? ddmmyyyyToIso(row.EndDate) ?? todayIso
  const end = row.CurrentPoste ? null : ddmmyyyyToIso(row.EndDate) ?? start
  return {
    id: row.id,
    title: row.Name ?? '',
    school: row.entreprise?.Name ?? '',
    start,
    end,
    type: inferFormationType(row.Name),
  }
}

/** Mappe une Competence (endpoint userCompetence) vers Skill. */
export function mapSkill(row) {
  return {
    id: row.id,
    name: row.CompetencesName ?? '',
    type: row.CompetencesType ?? 'Métier',
    // level : aucune donnée de niveau dans le modèle actuel → non renseigné
    suggested: false,
  }
}

/**
 * Champs recopiables tels quels lors d'une duplication (écriture directe
 * sur le modèle ParcoursPro via parcoursSerializer, fields="__all__").
 * Exclus : id, user, created_at (auto), ContactLink (relié par create()),
 * dates (converties), Name (suffixé), champs read-only du serializer.
 */
const DUPLICABLE_FIELDS = [
  'ParcoursType',
  'CurrentPoste',
  'City',
  'ContractType',
  'Statut',
  'CollectiveAgre',
  'Seniority',
  'GeneralInfo',
  'TyHours',
  'SubTHours',
  'hours',
  'Gmsalary',
  'GmsalaryF',
  'FinancialArrangement',
  'GrossAnnualIncome',
  'Advantage',
  'Entreprise',
  'Country',
  'KeyCompetencies',
  'data',
]

/**
 * Construit le payload de création d'une copie d'une ligne ParcoursPro
 * (POST sur crudParcoursProOperations). Pur et testable.
 * - Name suffixé " (copie)"
 * - dates reconverties en ISO (DRF accepte l'ISO en entrée alors qu'il
 *   sérialise en DD-MM-YYYY en sortie)
 * - champs null/vides omis (même logique que le formulaire de création)
 */
export function buildDuplicatePayload(row, copySuffix = ' (copie)') {
  const payload = { Name: `${row.Name ?? ''}${copySuffix}` }
  DUPLICABLE_FIELDS.forEach((field) => {
    const value = row[field]
    if (value !== null && value !== undefined && value !== '') payload[field] = value
  })
  const begin = ddmmyyyyToIso(row.BeginDate)
  const end = ddmmyyyyToIso(row.EndDate)
  if (begin) payload.BeginDate = begin
  if (end) payload.EndDate = end
  return payload
}

/**
 * Convertit les trois flux de la page (results des fetchs existants)
 * vers les props de <ParcoursPro/>.
 */
export function mapParcoursPro({ experiences = [], formations = [], skills = [] }, todayIso = new Date().toISOString().slice(0, 10)) {
  return {
    experiences: experiences.map((r) => mapExperience(r, todayIso)),
    formations: formations.map((r) => mapFormation(r, todayIso)),
    skills: skills.map(mapSkill),
  }
}
